import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { SESSION_CONSTANT } from "../../shared/utils/SessionConstants";
import { ENV } from "../../config";
import { getAuthToken } from "./SessionStorageService";
import { msalInstance, API_SCOPE } from "./msalInstance";
import { emitSessionExpired } from "../../utils/sessionEvents";
import { TAX_YEAR_HEADER, getCurrentTaxYear } from "./TaxYearHeader";

const BASE_URL = ENV.API_URL;

// Shared across every ApiService instance (each feature service extends ApiService
// and gets its own axios instance/interceptors) so concurrent 401s from a burst of
// parallel requests trigger exactly one refresh-token POST, not one per request.
let legacyRefreshPromise: Promise<string | undefined> | null = null;

const getSharedLegacyRefresh = (requestHeaders: any): Promise<string | undefined> => {
  if (!legacyRefreshPromise) {
    legacyRefreshPromise = Promise.resolve()
      .then(() => require("../../features/AuthenticateView/services/LoginService"))
      .then(({ refreshToken }) => refreshToken(requestHeaders))
      .finally(() => {
        legacyRefreshPromise = null;
      });
  }
  return legacyRefreshPromise;
};

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  extractData?: boolean;
  skipAuthHeaders?: boolean;
  _retry?: boolean;
}

class ApiService {
  private readonly api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: BASE_URL,
    });

    // Request Interceptor
    this.api.interceptors.request.use(
      async (config) => {
        const skipAuth = (config as ExtendedAxiosRequestConfig).skipAuthHeaders;

        if (!skipAuth) {
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            // User is logged in via Azure AD — acquire the API token
            try {
              const response = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              config.headers.Authorization = `Bearer ${response.accessToken}`;
            } catch (error) {
              if (error instanceof InteractionRequiredAuthError) {
                // Silent acquisition failed — fall back to popup
                try {
                  const response = await msalInstance.acquireTokenPopup({
                    scopes: [API_SCOPE],
                  });
                  config.headers.Authorization = `Bearer ${response.accessToken}`;
                } catch (popupError) {
                  console.error("[MSAL] Token acquisition failed:", popupError);
                  // AAD interaction is required and popup acquisition also failed —
                  // the session is confirmed dead, not just a transient hiccup.
                  // Don't send the request with a stale token; signal expiry instead.
                  emitSessionExpired();
                }
              } else {
                console.error("[MSAL] Silent token acquisition failed:", error);
                // Fall back to localStorage token so the request isn't sent unauthenticated
                const AUTH_TOKEN = getAuthToken();
                if (AUTH_TOKEN) config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
              }
            }
          } else {
            // No Azure AD account — use the existing JWT token from storage
            const AUTH_TOKEN = getAuthToken();
            config.headers.Authorization = `Bearer ${AUTH_TOKEN}`;
          }
        }

        // Selected tax year on every call (FR-19.8); the backend stamps new records with it.
        const taxYear = getCurrentTaxYear();
        if (taxYear) {
          config.headers[TAX_YEAR_HEADER] = taxYear;
        }

        if (config.data instanceof FormData) {
          delete config.headers["Content-Type"];
        } else if (config.data !== undefined) {
          config.headers["Content-Type"] = "application/json";
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalRequest = error?.config ?? error?.response?.config;
        const message =
          error?.response?.data || error?.message || "An error occurred";

        if (status === 401 && originalRequest && !originalRequest._retry) {
          // Don't attempt refresh for the login endpoint itself — would cause a loop
          if (originalRequest.url?.includes("auth/login")) {
            return Promise.reject(error);
          }

          // SSO path: if MSAL has an account, get a fresh token silently and retry
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            originalRequest._retry = true;
            try {
              const tokenResponse = await msalInstance.acquireTokenSilent({
                scopes: [API_SCOPE],
                account: accounts[0],
              });
              originalRequest.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              return this.api.request(originalRequest);
            } catch (msalError) {
              console.error("[MSAL] Silent refresh failed on 401 retry:", msalError);
              emitSessionExpired();
              return Promise.reject(error);
            }
          }

          // Don't attempt refresh if the refresh URL is not configured
          if (!ENV.API_URL || !ENV.APPLICATION_AUTHENTICATION_REFRESH_URL) {
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          try {
            const refreshedAccessToken = await getSharedLegacyRefresh(
              originalRequest.headers,
            );
            const accessToken =
              refreshedAccessToken ?? localStorage.getItem(SESSION_CONSTANT.TOKEN_KEY);

            if (accessToken) {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return this.api.request(originalRequest);
          } catch (refreshError: any) {
            // Only signal expiry when the refresh endpoint itself confirmed 401 —
            // a network/transient failure calling the refresh endpoint should not
            // force the session-expired modal.
            if (refreshError?.response?.status === 401) {
              emitSessionExpired();
            }
            return Promise.reject(refreshError);
          }
        }

        if (
          (status === 401 && !originalRequest) ||
          String(message).includes("UnAuthorizedCustomException")
        ) {
          localStorage.removeItem(SESSION_CONSTANT.TOKEN_KEY);
          window.location.replace("/login");
        }

        // Reached when a 401 didn't match the retry branch above — either a
        // second 401 on the same request after _retry was already set, or a
        // 401 with no originalRequest. Both mean the session is dead; signal it.
        if (status === 401) {
          emitSessionExpired();
        }

        return Promise.reject(message);
      },
    );
  }

  // Returns full AxiosResponse — use when headers/status are needed
  protected async makeRequest<T>(
    endpoint: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;

  // Returns response.data directly — use for standard JSON endpoints
  protected async makeRequest<T>(
    endpoint: string,
    options: ExtendedAxiosRequestConfig & { extractData: true },
  ): Promise<T>;

  protected async makeRequest<T>(
    endpoint: string,
    options?: ExtendedAxiosRequestConfig,
  ): Promise<AxiosResponse<T> | T> {
    try {
      const response = await this.api.request<T>({
        url: endpoint,
        method: options?.method ?? "GET",
        data: options?.data,
        headers: options?.headers,
        params: options?.params,
        responseType: options?.responseType ?? "json",
        skipAuthHeaders: options?.skipAuthHeaders,
      } as ExtendedAxiosRequestConfig);

      return options?.extractData ? response.data : response;
    } catch (e: any) {
      return Promise.reject(e ?? "An error occurred");
    }
  }
}

export default ApiService;
